import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import {
  Divider,
  Alert,
  CircularProgress,
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Snackbar,
  LinearProgress,
  Paper,
  Grid,
} from '@mui/material';
import { Print as PrintIcon, Draw as DrawIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getLeaseByIdForCompletePreview } from '../api';
import { ThemeWrapper } from '../../../LeaseProcess/Flow/StepperComponents/sign-lease/utils';
import { formatDate } from '../helpers';
import { Step1, Step2, Step3, Step4, Step5, Step6 } from '../../../LeaseProcess/Flow/StepperComponents/lease/components/steps/3-PreviewLeaseByManager/Steps';
import { Step7 } from './Steps';
import '../leases.scss';

const PreviewExistingLease = ({ leaseId, handleClose }) => {
  const [leaseData, setLeaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Preparing document...');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // Blank copy for signing by hand: no signatures, dates or initials filled in.
  const [blankMode, setBlankMode] = useState(false);
  const leaseFormRef = useRef(null);

  useEffect(() => {
    const fetchLease = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!leaseId) {
          throw new Error('Lease ID is required');
        }
        const response = await getLeaseByIdForCompletePreview(leaseId);
        if (response.success) {
          setLeaseData(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch lease');
        }
      } catch (err) {
        setError(err.message || 'Error fetching lease');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to fetch lease',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLease();
  }, [leaseId]);

  const handleDownloadPDF = async ({ blank = false } = {}) => {
    if (leaseFormRef.current) {
      try {
        setIsGeneratingPDF(true);
        setShowProgress(true);
        setProgressMessage('Preparing document for download...');
        setProgress(5);
        if (blank) {
          setBlankMode(true);
          // Let the blank signature/initials boxes paint before capturing.
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        }

        const pdf = new jsPDF('p', 'mm', 'a4');
        const stepDivs = leaseFormRef.current.querySelectorAll('.lease-step');
        const totalSteps = stepDivs.length;

        for (let i = 0; i < totalSteps - 1; i++) {
          setProgressMessage(`Generating page ${i + 1} of ${totalSteps}...`);
          const stepDiv = stepDivs[i];
          setProgress(5 + Math.round((i / totalSteps) * 45));
          const canvas = await html2canvas(stepDiv, {
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
          });
          setProgress(50 + Math.round((i / totalSteps) * 30));
          const imgData = canvas.toDataURL('image/jpeg', 0.9);
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          if (i > 0) {
            pdf.addPage();
          }
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
          setProgress(80 + Math.round((i / totalSteps) * 20));
        }
        setProgressMessage('Finalizing PDF...');
        setProgress(95);
        await new Promise((resolve) => setTimeout(resolve, 500));
        setProgressMessage('Download starting...');
        setProgress(100);
        pdf.save(blank ? `Lease-Blank-For-Signing-${leaseId}.pdf` : `Lease-Preview-${leaseId}.pdf`);
        setBlankMode(false);

        setTimeout(() => {
          setShowProgress(false);
          setProgress(0);
          setIsGeneratingPDF(false);
          handleClose()
        }, 1000);
      } catch (error) {
        console.error('Error generating PDF:', error);
        setBlankMode(false);
        setProgressMessage('Error generating PDF. Please try again.');
        setTimeout(() => {
          setShowProgress(false);
          setProgress(0);
          setIsGeneratingPDF(false);
        }, 3000);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          Loading lease preview...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  const steps = [
    { label: 'Step 1', component: <Step1 lease={leaseData} /> },
    { label: 'Step 2', component: <Step2 lease={leaseData} /> },
    { label: 'Step 3', component: <Step3 lease={leaseData} /> },
    { label: 'Step 4', component: <Step4 lease={leaseData} /> },
    { label: 'Step 5', component: <Step5 lease={leaseData} /> },
    { label: 'Step 6', component: <Step6 lease={leaseData} blank={blankMode} /> },
    { label: 'Step 7', component: <Step7 lease={leaseData} /> },
  ];

  return (
    <ThemeWrapper>
      <Box sx={{ backgroundColor: '#F3F3E0', pt: 3, pb: 8 }}>
        <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
          {/* Progress Indicator */}
          {showProgress && (
            <Snackbar
              open
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ width: '100%' }}
            >
              <Paper sx={{ p: 2, width: '100%', maxWidth: '500px' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {progressMessage}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
                >
                  {progress}%
                </Typography>
              </Paper>
            </Snackbar>
          )}

          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#183B4E' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" color="primary.contrastText">
                  Lease: {leaseData?.property_details?.property_name}
                  {leaseData?.property_details?.unit_no && `, Unit ${leaseData?.property_details?.unit_no}`}
                </Typography>

                <Typography variant="body2" color="primary.contrastText" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                  {formatDate(leaseData?.lease_details?.start_date)} to {formatDate(leaseData?.lease_details?.end_date)}
                </Typography>

              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Button
                    startIcon={<PrintIcon />}
                    sx={{ color: 'primary.contrastText', textTransform: 'none' }}
                    onClick={() => handleDownloadPDF()}
                    disabled={isGeneratingPDF}
                  >
                    {isGeneratingPDF && !blankMode ? 'Generating PDF...' : 'Download PDF'}
                  </Button>
                  <Button
                    startIcon={<DrawIcon />}
                    title="Unsigned copy with empty signature, date and initials boxes, to print and sign by hand"
                    sx={{ color: 'primary.contrastText', textTransform: 'none' }}
                    onClick={() => handleDownloadPDF({ blank: true })}
                    disabled={isGeneratingPDF}
                  >
                    {isGeneratingPDF && blankMode ? 'Generating PDF...' : 'Download Blank Lease'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Main Content */}
          <Card className="shadow-lg p-5 lease-form-existing" style={{ backgroundColor: 'white' }}>
            <Card.Body>
              <div ref={leaseFormRef}>
                {steps.map((step, index) => (
                  <div key={index} className="mb-4 lease-step">
                    {step.component}
                    {index < steps.length - 1 && <Footer lease={leaseData} page={index + 1} blank={blankMode} />}
                    {index < steps.length - 1 && (
                      <Divider sx={{ my: 4, borderColor: 'gray' }} />
                    )}
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Container>
      </Box>
    </ThemeWrapper>
  );
};

const Footer = ({ lease, page, blank = false }) => {
  const landlordName = lease?.manager
    ? `${lease.manager.first_name || ''} ${lease.manager.last_name || ''}`.trim()
    : '';
  const landlordInitials = landlordName
    ? landlordName
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
    : 'N/A';

  const tenantInitials = lease?.tenants?.length > 0
    ? lease.tenants
      .map((tenant) => {
        const manual = (tenant.initials || '').trim();
        if (manual) {
          const pages = tenant.initialed_pages || [];
          return !pages.length || page == null || pages.includes(page) ? manual : '';
        }
        return (tenant.name || '')
          .split(' ')
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase())
          .join('');
      })
      .filter(Boolean)
      .join(', ')
    : 'N/A';

  return (
    <Box className="lease-form-footer" display="flex" justifyContent="flex-end" alignItems="center">
      <Box textAlign="center" mr={2}>
        <Typography variant="caption" fontSize="0.7rem">
          INITIALS
        </Typography>
      </Box>
      <Table size="small" sx={{ width: '120px', border: '1px solid', borderColor: 'grey.500' }}>
        <TableBody>
          <TableRow>
            <TableCell
              align="center"
              sx={{ borderRight: '1px solid', borderColor: 'grey.500', fontSize: '0.8rem', p: 0.5 }}
            >
              Landlord
            </TableCell>
            <TableCell align="center" sx={{ fontSize: '0.8rem', p: 0.5 }}>
              Tenant(s)
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              align="center"
              sx={{ borderRight: '1px solid', borderColor: 'grey.500', fontSize: '0.9rem', p: 0.5 }}
            >
              {blank ? '' : landlordInitials}
            </TableCell>
            <TableCell align="center" sx={{ fontSize: '0.9rem', p: 0.5, height: blank ? 28 : undefined }}>
              {blank ? '' : tenantInitials}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export { PreviewExistingLease };